// @flow
import { Icon } from 'antd';
import Button from 'antd/lib/button';
import InputNumber from 'antd/lib/input-number';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { FormField } from '../../components/Form';
import Grid, { GridItem } from '../../components/layout/Grid';
import ListTable, { Row } from '../../components/ui/ListTable';

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RowHeader = styled.div`
  width: 100%;
  background: #f0f4f8;
  color: #838d96;
  padding: 11px 15px;
  border-top: 1px solid #e4ebf2;
`;

const StyledGrid = styled(Grid)`
  margin-bottom: 20px;
`;

const StyledIcon = styled(Icon)`
  cursor: pointer;
  color: red!important;
  align-self: center;
`;

const StyledButton = styled(Button)`
  margin-top: 20px;
  ${props =>
    props.type === 'primary' &&
    css`
      margin-bottom: 20px;
    `};
`;

type Props = {
  trailerMileage?: number;
  trailerWeight?: number;
  cargoMileage?: {
    id: number;
    mileage: number;
    weight: number;
  }[];
  disabled?: boolean;
  distance?: number;
  onChange?: (item: {
    trailerMileage?: number;
    trailerWeight?: number;
    cargoMileage?: {
      mileage: number;
      weight: number;
    }[];
  })=> void;
};

const columns = [
  {
    title: 'Пробег с грузом',
    key: 'mileage',
    width: '250px',
  },
  {
    title: 'Масса прицепа/груза',
    key: 'weight',
    width: '100px',
  },
  {
    key: 'delete',
    width: '100px',
  }
];

/** Перевозка груза */
const ShippingForm: FC<Props> = ({
  cargoMileage = [],
  trailerMileage,
  trailerWeight,
  distance,
  onChange,
  disabled = false
})=> {
  const [isFormActive, setIsFormActive] = useState(!!trailerMileage || !!cargoMileage?.length || !!trailerWeight);
  const [preparedCargoMileage, setPreparedCargoMileage] = useState(
    [
      { mileage: trailerMileage ?? 0, weight: trailerWeight ?? 0 }, 
      ...(cargoMileage?.length ? cargoMileage : [{ mileage: 0, weight: 0 }])
    ]
  );

  const deleteHandler = (index: number)=> {
    const prep = [...preparedCargoMileage];
    prep.splice(index, 1);
    setPreparedCargoMileage(prep);
    onChange({
      trailerMileage: prep[0].mileage,
      trailerWeight: prep[0].weight,
      cargoMileage: prep.slice(1)
    });
  }

  const addNewRowHandler = () => {
    if(preparedCargoMileage.slice(1).reduce((c, e)=> (e?.mileage ?? 0) + c, 0) < distance){
      setPreparedCargoMileage(prev => [...prev, { mileage: 0, weight: 0 }]);
    }
  }

  const changeHandler = (index: number, key: 'mileage' | 'weight', value: number)=> {
    const preparedValue = !Number.isNaN(+value?.replace(',', '.')) ? +value?.replace(',', '.') : 0
    const prep = [...preparedCargoMileage];

    if(key === 'mileage'){
      prep[index][key]= preparedValue;
    } else {
      prep[index][key] = preparedValue
    }
    
    setPreparedCargoMileage(prep);
    onChange({
      trailerMileage: prep[0].mileage,
      trailerWeight: prep[0].weight,
      cargoMileage: prep.slice(1)
    });
    if(index > 0 && prep[prep.length - 1]?.mileage !== 0 && prep.slice(1).reduce((c, e)=> (e?.mileage ?? 0) + c, 0) < distance){
      addNewRowHandler();
    }
  }

  const ShippingFormRow = ({item, index})=> {
    const [current, setCurrent] = useState({
      mileage: item?.mileage,
      weight: item?.weight
    });

    const RestDistance = ()=> {
      if(index === 0 || disabled){
        return null
      }
      
      const currentRestDistance = (distance - preparedCargoMileage?.slice(1, index + 1)
        ?.reduce((c, e)=> (e.mileage || 0) + c, 0));
        
      return (
        <div style={{color: currentRestDistance > 0 ? 'green' : 'red'}}>
          {currentRestDistance >= 0 ? 'Осталось' : 'Превышено'} {Math.abs(currentRestDistance).toFixed(1)} км
        </div>
      )
    }

    return(
      <>
        <div>
          <FormField 
            name={`${index}_mileage`}
            required={current.mileage === ''}
          >
            {({name}) => (
              <InputWrapper>
                <InputNumber
                  name={name}
                  style={{width: '100%'}}
                  value={current.mileage}
                  decimalSeparator=","
                  min={0}
                  disabled={disabled}
                  placeholder='Введите пробег'
                  max={index === 0 ? distance : undefined}
                  onChange={(e) => setCurrent({...current, mileage: e})}
                  onBlur={(e) => changeHandler(index, 'mileage', e.target.value)}
                  precision={1}
                />
                <div>км</div>
              </InputWrapper>
            )}
          </FormField>
          <RestDistance/>
        </div>
        <FormField
          required={current.weight === ''}
          name={`${index}_weight`}
        >
          {({name}) => (
            <InputWrapper>
              <InputNumber
                name={name}
                style={{minWidth: '100%'}}
                value={current.weight}
                decimalSeparator=","
                min={0}
                disabled={disabled}
                placeholder='Введите массу прицепа'
                precision={2}
                onChange={(e) => setCurrent({...current, weight: e})}
                onBlur={(e) => changeHandler(index, 'weight', e.target.value)}
              />
              <div>т</div>
            </InputWrapper>
          )}
        </FormField>
        {index > 0 
          && index !== preparedCargoMileage?.length - 1 
          && !disabled 
          && <StyledIcon
                type="close"
                onClick={() => deleteHandler(index)}
              />
        }
      </>
    )
  }

  const toggleShowForm = ()=> {
    if(isFormActive){
      onChange({
        trailerMileage: null,
        trailerWeight: null,
        cargoMileage: []
      })
      setIsFormActive(false);
      setPreparedCargoMileage([
        { mileage: 0, weight: 0 },
        { mileage: 0, weight: 0 },
      ]);
    } else {
      setIsFormActive(true);
    }
  }

  return (
    <StyledGrid gutter="16px" rowGutter="20px">
      <GridItem fullWidth>
        {!disabled && <StyledButton
          type={isFormActive ? 'primary' : 'default'}
          onClick={toggleShowForm}
        >
          {isFormActive
            ? 'Отменить расчет перевозки груза'
            : 'Перевозка груза'}
        </StyledButton>}
      </GridItem>
      <GridItem fullWidth>
        {isFormActive && (
          <ListTable
            columns={columns}
            data={distance ? preparedCargoMileage : []}
            renderRow={(item, index, columns)=> {
              const columnWidths = columns?.map(e=> e.props.width)
              
              if(index === 0) {
                return (
                  <React.Fragment key={item.id ?? index}>
                    <RowHeader>
                      Прицеп
                    </RowHeader>
                    <Row cols={columnWidths}>
                      <ShippingFormRow index={index} item={item}/>
                    </Row>
                  </React.Fragment>
                )
              } else if (index === 1){
                return (
                  <React.Fragment key={item.id ?? index}>
                    <RowHeader>
                      Грузы
                    </RowHeader>
                    <Row cols={columnWidths}>
                      <ShippingFormRow index={index} item={item}/>
                    </Row>
                  </React.Fragment>
                )
              }
              return (
                <Row cols={columnWidths} key={item.id ?? index}>
                  <ShippingFormRow item={item} index={index}/>
                </Row>
              )
            }}
          ></ListTable>
        )}
      </GridItem>
    </StyledGrid>
  );
}

export default ShippingForm;
